
import { Component, Vue } from 'vue-property-decorator'
import UploadExcel from '@/components/UploadExcel/index.vue'
import { exportJson2Excel } from '@/utils/excel'
import { createUserFromAdmin, CreatedUser } from '@/api/adminUsers'

// Fixed, technical column keys. Deliberately NOT translated: the operator can
// download the template with the panel in one language and fill it in with the
// panel in another, and a localised header would then no longer be recognised.
// The meaning of each column is explained in the table above the upload box.
const COLUMNS = ['email', 'firstName', 'lastName', 'countryCode', 'phoneNumber']
const MAX_ROWS = 500

type RowStatus = 'ready' | 'invalid' | 'creating' | 'created' | 'failed'

interface BulkRow {
  rowNo: number
  email: string
  firstName: string
  lastName: string
  countryCode: string
  phoneNumber: string
  status: RowStatus
  message: string
  userId: string
}

@Component({ name: 'AdminUsers', components: { UploadExcel } })
export default class extends Vue {
  private activeTab = 'single'

  /* ---------------------------------------------------------- single -- */
  private loading = false
  private created: CreatedUser[] = []

  private form = {
    email: '',
    firstName: '',
    lastName: '',
    countryCode: '',
    phoneNumber: ''
  }

  // Country code and number travel together; the backend refuses half a phone,
  // so the form says so before the round trip.
  private validatePhonePair = (_rule: any, _value: string, callback: Function) => {
    const hasCode = !!this.form.countryCode.trim()
    const hasNumber = !!this.form.phoneNumber.trim()
    if (hasCode !== hasNumber) {
      callback(new Error(this.$t('adminUsers.phonePairRequired') as string))
      return
    }
    callback()
  }

  // A getter rather than a plain field so the messages are resolved in the
  // active language, and re-resolved when the operator switches language.
  private get rules() {
    return {
      email: [
        { required: true, message: this.$t('adminUsers.emailRequired'), trigger: 'blur' },
        { type: 'email', message: this.$t('adminUsers.emailInvalid'), trigger: 'blur' }
      ],
      firstName: [
        { required: true, message: this.$t('adminUsers.firstNameRequired'), trigger: 'blur' }
      ],
      countryCode: [{ validator: this.validatePhonePair, trigger: 'blur' }],
      phoneNumber: [{ validator: this.validatePhonePair, trigger: 'blur' }]
    }
  }

  private submit() {
    (this.$refs.form as any).validate(async(valid: boolean) => {
      if (!valid) return
      this.loading = true
      try {
        const res = await createUserFromAdmin(this.payloadFrom(this.form))
        // The request wrapper resolves with the whole axios response and does
        // not unwrap the API envelope, so the created user is one level deeper
        // than it looks: response.data is { status, data }.
        const data: CreatedUser = res.data.data
        this.created.unshift(data)
        this.$message.success(this.$t('adminUsers.createdMessage', { email: data.email }) as string)
        this.resetForm()
      } catch (err: any) {
        this.$message.error(this.messageFor(err))
      } finally {
        this.loading = false
      }
    })
  }

  private resetForm() {
    this.form = { email: '', firstName: '', lastName: '', countryCode: '', phoneNumber: '' }
    const f = this.$refs.form as any
    if (f) f.clearValidate()
  }

  /* ------------------------------------------------------------ bulk -- */
  private rows: BulkRow[] = []
  private bulkRunning = false
  private bulkDone = 0

  private get columnDocs() {
    return [
      { key: 'email', required: true, desc: 'adminUsers.docEmail' },
      { key: 'firstName', required: true, desc: 'adminUsers.docFirstName' },
      { key: 'lastName', required: false, desc: 'adminUsers.docLastName' },
      { key: 'countryCode', required: false, desc: 'adminUsers.docCountryCode' },
      { key: 'phoneNumber', required: false, desc: 'adminUsers.docPhoneNumber' }
    ]
  }

  private get readyRows() {
    return this.rows.filter(r => r.status === 'ready')
  }

  private get invalidRows() {
    return this.rows.filter(r => r.status === 'invalid')
  }

  private get finishedRows() {
    return this.rows.filter(r => r.status === 'created' || r.status === 'failed')
  }

  private get progressPercent() {
    const total = this.bulkDone + this.readyRows.length + this.rows.filter(r => r.status === 'creating').length
    return total ? Math.round((this.bulkDone / total) * 100) : 0
  }

  private downloadTemplate() {
    // Two example rows: one with a phone, one without, so it is visible that
    // the phone columns may simply be left empty.
    const data = [
      ['ornek1@site.com', 'Ada', 'Lovelace', '', ''],
      ['ornek2@site.com', 'Alan', 'Turing', '49', '15112345678']
    ]
    exportJson2Excel(COLUMNS, data, 'wewalk-user-template')
  }

  private beforeExcelUpload(file: File) {
    const withinSize = file.size / 1024 / 1024 < 5
    if (!withinSize) {
      this.$message.error(this.$t('adminUsers.fileTooLarge') as string)
      return false
    }
    return true
  }

  private handleExcelSuccess({ results }: { header: string[], results: any[] }) {
    if (!results || !results.length) {
      this.$message.warning(this.$t('adminUsers.fileEmpty') as string)
      return
    }
    if (results.length > MAX_ROWS) {
      this.$message.error(this.$t('adminUsers.tooManyRows', { max: MAX_ROWS }) as string)
      return
    }

    // Header lookup is case- and space-insensitive so a file that came back
    // from a distributor with "Email" or " email " still lines up.
    const pick = (row: any, key: string) => {
      const found = Object.keys(row).find(
        k => k.trim().toLowerCase() === key.toLowerCase()
      )
      return found === undefined ? '' : String(row[found] ?? '').trim()
    }

    const seen = new Set<string>()
    this.rows = results.map((raw: any, i: number) => {
      const row: BulkRow = {
        // +2: one for the header row, one because spreadsheets start at 1, so
        // this is the line number the operator sees in Excel.
        rowNo: i + 2,
        email: pick(raw, 'email').toLowerCase(),
        firstName: pick(raw, 'firstName'),
        lastName: pick(raw, 'lastName'),
        countryCode: pick(raw, 'countryCode').replace(/[^0-9]/g, ''),
        phoneNumber: pick(raw, 'phoneNumber').replace(/[^0-9]/g, ''),
        status: 'ready',
        message: '',
        userId: ''
      }

      const invalid = (msgKey: string) => {
        row.status = 'invalid'
        row.message = this.$t(msgKey) as string
      }

      if (!row.email) invalid('adminUsers.emailRequired')
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(row.email)) invalid('adminUsers.emailInvalid')
      else if (seen.has(row.email)) invalid('adminUsers.duplicateInFile')
      else if (!row.firstName) invalid('adminUsers.firstNameRequired')
      else if (!row.countryCode !== !row.phoneNumber) invalid('adminUsers.phonePairRequired')

      if (row.email) seen.add(row.email)
      return row
    })

    this.bulkDone = 0
    this.$message.success(
      this.$t('adminUsers.fileLoaded', { n: this.rows.length }) as string
    )
  }

  private confirmBulk() {
    this.$confirm(
      this.$t('adminUsers.bulkConfirmBody', { n: this.readyRows.length }) as string,
      this.$t('adminUsers.bulkConfirmTitle') as string,
      {
        confirmButtonText: this.$t('adminUsers.continue') as string,
        cancelButtonText: this.$t('adminUsers.cancel') as string,
        type: 'warning'
      }
    )
      .then(() => this.runBulk())
      .catch(() => undefined)
  }

  private async runBulk() {
    this.bulkRunning = true
    this.bulkDone = 0

    // One at a time on purpose. These calls create accounts, and a burst of
    // parallel requests would make a partial failure much harder to read back
    // than a list that stops where it stopped.
    for (const row of this.rows) {
      if (row.status !== 'ready') continue
      row.status = 'creating'
      try {
        const res = await createUserFromAdmin(this.payloadFrom(row))
        const data: CreatedUser = res.data.data
        row.status = 'created'
        row.userId = data.userId
        row.message = ''
        this.created.unshift(data)
      } catch (err: any) {
        row.status = 'failed'
        row.message = this.messageFor(err)
      }
      this.bulkDone++
    }

    this.bulkRunning = false
    const ok = this.rows.filter(r => r.status === 'created').length
    const failed = this.rows.filter(r => r.status === 'failed').length
    if (failed) {
      this.$message.warning(this.$t('adminUsers.bulkDoneWithErrors', { ok, failed }) as string)
    } else {
      this.$message.success(this.$t('adminUsers.bulkDone', { ok }) as string)
    }
  }

  private downloadResults() {
    const header = [...COLUMNS, 'status', 'message', 'userId']
    const data = this.rows.map(r => [
      r.email, r.firstName, r.lastName, r.countryCode, r.phoneNumber,
      this.$t('adminUsers.status_' + r.status) as string, r.message, r.userId
    ])
    exportJson2Excel(header, data, 'wewalk-user-results')
  }

  private clearBulk() {
    this.rows = []
    this.bulkDone = 0
  }

  private rowClass({ row }: { row: BulkRow }) {
    if (row.status === 'invalid' || row.status === 'failed') return 'row-bad'
    if (row.status === 'created') return 'row-good'
    return ''
  }

  private statusTag(status: RowStatus) {
    if (status === 'created') return 'success'
    if (status === 'invalid' || status === 'failed') return 'danger'
    if (status === 'creating') return 'warning'
    return 'info'
  }

  /* ----------------------------------------------------------- shared -- */
  private payloadFrom(src: { email: string, firstName: string, lastName: string, countryCode: string, phoneNumber: string }) {
    return {
      email: src.email.trim(),
      firstName: src.firstName.trim(),
      lastName: src.lastName.trim(),
      countryCode: src.countryCode.trim(),
      phoneNumber: src.phoneNumber.trim()
    }
  }

  // The backend answers with a code, not a sentence; these are the ones an
  // operator can actually act on.
  private messageFor(err: any) {
    const code = err?.response?.data?.status?.message || err?.message
    const known: Record<string, string> = {
      admin_create_user_email_taken: 'adminUsers.errorEmailTaken',
      admin_create_user_phone_taken: 'adminUsers.errorPhoneTaken',
      admin_create_user_email_invalid: 'adminUsers.errorEmailInvalid',
      admin_create_user_first_name_required: 'adminUsers.errorFirstNameRequired',
      admin_create_user_phone_incomplete: 'adminUsers.errorPhoneIncomplete'
    }
    if (known[code]) return this.$t(known[code]) as string
    // An unrecognised code is shown as-is rather than hidden behind a generic
    // sentence, so an unexpected backend error stays diagnosable.
    return code || (this.$t('adminUsers.errorGeneric') as string)
  }
}
